import { useEffect, useId } from 'react';
import { useFormState } from './formContext';
import type { FieldControl, FormFieldProps } from './types';

export function FormField({ name, id: suppliedId, label, required = false, optionalLabel = false,
  help, errors: suppliedErrors, children, className, onReveal }: FormFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? `ui-field-${generatedId}`;
  const form = useFormState();
  const register = form?.register;
  const errors = suppliedErrors ?? form?.errors.fields[name] ?? [];
  const helpId = `${id}-help`;
  const errorId = `${id}-errors`;
  const hasHelp = help !== undefined && help !== null;
  useEffect(() => register?.(name, {
    id, label,
    control: () => document.getElementById(id),
    focus: () => {
      onReveal?.();
      if (onReveal) requestAnimationFrame(() => document.getElementById(id)?.focus());
      else document.getElementById(id)?.focus();
    },
  }), [register, name, id, label, onReveal]);
  const control: FieldControl = {
    id, name, required,
    'aria-required': required ? true : undefined,
    'aria-invalid': errors.length ? true : undefined,
    'aria-describedby': [hasHelp ? helpId : '', errors.length ? errorId : ''].filter(Boolean).join(' ') || undefined,
  };
  return <div className={`ui-field ${className ?? ''}`}>
    <label className="ui-field__label" htmlFor={id}>{label}
      {required ? <> <span className="ui-required" aria-hidden="true">*</span></>
        : optionalLabel ? <span className="ui-field__optional"> (optional)</span> : null}
    </label>
    {children(control)}
    {hasHelp && <div className="ui-field__help" id={helpId}>{help}</div>}
    {errors.length > 0 && <div className="ui-field__errors" id={errorId}>
      {errors.map((error, index) => <p key={`${index}:${error}`}>Error: {error}</p>)}
    </div>}
  </div>;
}
